/**
 * User Dropdown Component
 * Dropdown menu using shadcn DropdownMenu with Radix primitives
 * Desktop: Profile, Settings, Theme, Logout
 * Mobile: All nav links + Profile, Settings, Theme, Logout
 */

import {
  Bell,
  MessageCircleMore,
  // Moon,
  Plus,
  Power,
  Search,
  Settings,
  // Sun,
  Users,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { LogoutConfirmationModal } from "@/components/modals/logoutConfirmationModal"
import { Avatar } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  // DropdownMenuSub,
  // DropdownMenuSubContent,
  // DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useChatUnreadCount } from "@/features/chats/hooks/useChatUnreadCount"
import { useUnreadCount } from "@/features/notifications/hooks/useNotifications"
import { getAssetUrl } from "@/utils/assets"
import { UserIcon } from "../../components/icons/index"

interface UserDropdownProps {
  isMobile?: boolean
  /** Callback when notifications dropdown open state changes (mobile only) */
  onNotificationsOpenChange?: (open: boolean) => void
  /** Callback to open the search overlay */
  onSearchOpen?: () => void
}

export function UserDropdown({
  isMobile = false,
  onNotificationsOpenChange,
  onSearchOpen,
}: UserDropdownProps) {
  const { t } = useTranslation("dashboard")
  const navigate = useNavigate()
  const user = useAuthStore(state => state.user)
  const logout = useAuthStore(state => state.logout)
  // const { theme, setTheme } = useTheme()
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const { data: unreadCount } = useUnreadCount()
  const { data: chatUnreadCount } = useChatUnreadCount()

  // Mobile-only: Handle notifications open change
  const handleNotificationsOpenChange = isMobile ? onNotificationsOpenChange : undefined

  const userImageUrl =
    user?.profile_photos?.find(p => p.is_cover_image || p.isCover)?.filePath || undefined

  const defaultAvatarUrl = getAssetUrl("upload-bg.jpeg")

  const handleLogout = () => {
    try {
      logout()
      navigate("/login")
      setShowLogoutModal(false)
    } catch (error) {
      console.error("Logout error:", error)
    }
  }

  const navItems = [
    { label: t("navigation.home"), path: "/my-rituality" },
    { label: t("navigation.explore"), path: "/learn-and-practice" },
    { label: t("navigation.connect"), path: "/connect" },
    { label: t("navigation.myProfile"), path: "/profile" },
    { label: "Blog", href: "https://myrituality.com/blog/" },
  ]

  return (
    <>
      {/* Desktop: Avatar opens dropdown with user info and links */}
      {!isMobile && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 px-1 py-1 rounded-lg hover:bg-black/4 transition-colors cursor-pointer"
              aria-label="User menu"
            >
              <Avatar
                src={userImageUrl}
                fallback={user?.name || user?.email || "User"}
                fallbackSrc={defaultAvatarUrl}
                size="lg"
              />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="start"
            className="min-w-[340px] border-border bg-base rounded-[20px] p-5 me-6 flex flex-col gap-4"
          >
            {/* User Info Header */}
            <div className="flex items-center gap-3">
              <div className="flex flex-col">
                <span className="text-[15px] font-medium text-charcoal">
                  {user?.name || "User"}
                </span>
                <span className="text-[13px] text-body">@{user?.username || "username"}</span>
              </div>
            </div>

            {/* My Rituality */}
            <DropdownMenuItem
              onClick={() => navigate("/my-rituality")}
              className="cursor-pointer text-[14px] leading-[24px] font-[600] text-charcoal p-0"
            >
              <UserIcon className="!w-5 !h-5 mr-1" />
              {t("navigation.home")}
            </DropdownMenuItem>

            {/* Settings */}
            <DropdownMenuItem
              onClick={() => navigate("/settings")}
              className="cursor-pointer text-[14px] leading-[24px] font-[600] text-charcoal p-0"
            >
              <Settings className="!w-5.5 !h-6 mr-1" />
              {t("menu.settings")}
            </DropdownMenuItem>

            {/* Logout */}
            <DropdownMenuItem
              onClick={() => setShowLogoutModal(true)}
              variant="destructive"
              className="cursor-pointer text-[14px] leading-[24px] font-[600] text-brand-terracotta p-0"
            >
              <Power className="!w-6 !h-6" />
              {t("menu.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Mobile: Avatar opens dropdown with nav items */}
      {isMobile && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 px-1 py-1 rounded-lg hover:bg-black/4 transition-colors cursor-pointer"
              aria-label="User menu"
            >
              <Avatar
                src={userImageUrl}
                fallback={user?.name || user?.email || "User"}
                fallbackSrc={defaultAvatarUrl}
                size="lg"
              />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="min-w-[200px]">
            {/* Mobile: Show navigation items */}
            {navItems.map(item =>
              item.href ? (
                <DropdownMenuItem asChild key={item.href}>
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-sm font-normal text-charcoal px-2 py-1.5 outline-none cursor-pointer"
                  >
                    {item.label}
                  </a>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  key={item.path}
                  onClick={() => item.path && navigate(item.path)}
                  className="cursor-pointer text-sm font-normal text-charcoal"
                >
                  {item.label}
                </DropdownMenuItem>
              ),
            )}

            <DropdownMenuItem
              onClick={() => navigate("/chats")}
              className="cursor-pointer text-sm font-normal text-charcoal"
            >
              <MessageCircleMore className="w-4 h-4 mr-2" />
              {t("navigation.chats")}
              {(chatUnreadCount ?? 0) > 0 && (
                <span className="ml-auto w-5 h-5 bg-red text-white text-xs rounded-full flex items-center justify-center">
                  {(chatUnreadCount ?? 0) > 9 ? "9+" : chatUnreadCount}
                </span>
              )}
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={() => onSearchOpen?.()}
              className="cursor-pointer text-sm font-normal text-charcoal"
            >
              <Search className="w-4 h-4 mr-2" />
              {t("menu.search")}
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                handleNotificationsOpenChange?.(true)
              }}
              className="cursor-pointer text-sm font-normal text-charcoal"
            >
              <Bell className="w-4 h-4 mr-2" />
              {t("menu.notifications")}
              {(unreadCount || 0) > 0 && (
                <span className="ml-auto w-5 h-5 bg-red text-white text-xs rounded-full flex items-center justify-center">
                  {(unreadCount || 0) > 9 ? "9+" : unreadCount}
                </span>
              )}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Mobile: Create */}
            <DropdownMenuItem
              onClick={() => navigate("/communities/create")}
              className="cursor-pointer text-sm font-normal text-charcoal"
            >
              <Users className="w-4 h-4 mr-2" />
              {t("create.button")} {t("create.community")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigate("/rituals/create")}
              className="cursor-pointer text-sm font-normal text-charcoal"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t("create.button")} {t("create.ritual")}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Mobile: Settings */}
            <DropdownMenuItem
              onClick={() => navigate("/settings")}
              className="cursor-pointer text-sm font-normal text-charcoal"
            >
              <Settings className="w-4 h-4 mr-2" />
              {t("menu.settings")}
            </DropdownMenuItem>

            {/* Theme Selector - Submenu */}
            {/* <DropdownMenuSub>
              <DropdownMenuSubTrigger className="cursor-pointer text-sm font-normal text-charcoal">
                {theme === "light" ? (
                  <Sun className="w-4 h-4 mr-2" />
                ) : (
                  <Moon className="w-4 h-4 mr-2" />
                )}
                {t("menu.theme")}
                <span className="ml-auto text-xs text-ash">
                  {theme === "light" ? t("menu.light") : t("menu.dark")}
                </span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem
                  onClick={() => setTheme("light")}
                  className="cursor-pointer text-sm flex items-center justify-between"
                >
                  <span>{t("menu.light")}</span>
                  {theme === "light" && <span className="text-green">&#10003;</span>}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setTheme("dark")}
                  className="cursor-pointer text-sm flex items-center justify-between"
                >
                  <span>{t("menu.dark")}</span>
                  {theme === "dark" && <span className="text-green">&#10003;</span>}
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub> */}

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={() => setShowLogoutModal(true)}
              variant="destructive"
              className="cursor-pointer text-sm font-normal text-red"
            >
              <Power className="w-4 h-4 mr-2 " />
              {t("menu.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
      />
    </>
  )
}
